import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Scale, Menu, X, Linkedin, Twitter, Facebook, MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function PublicLayout() {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const NAV = [
    { to: "/", label: t("nav.home") },
    { to: "/attorney", label: t("nav.attorney") },
    { to: "/practice-areas", label: t("nav.practiceAreas") },
    { to: "/blog", label: t("nav.insights") },
    { to: "/contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    api.get("/public/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const firm = profile?.firm_name || "Wanar & Associates";

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      <header data-testid="public-navbar"
        className={`sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b transition-shadow duration-300 ${scrolled ? "border-border shadow-card" : "border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 gap-4">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
              <span className="h-10 w-10 bg-navy flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
                <Scale className="h-5 w-5 text-ivory group-hover:text-navy transition-colors duration-300" />
              </span>
              <span className="font-serif text-xl font-semibold text-navy tracking-tight">{firm}</span>
            </Link>
            <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
              {NAV.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={`nav-${item.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `text-sm font-medium tracking-wide transition-colors duration-300 ${isActive ? "text-gold-700" : "text-navy-700 hover:text-navy"}`}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/book-consultation" data-testid="nav-book-consultation-btn"
                className="inline-flex items-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-ivory transition-colors duration-300 font-semibold uppercase tracking-wider text-xs px-6 py-3">
                {t("nav.book")} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-navy" onClick={() => setMenuOpen(!menuOpen)} aria-label={t("nav.menu")}>
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === "/"}
                  className={({ isActive }) => `py-3 text-sm font-medium border-b border-muted ${isActive ? "text-gold-700" : "text-navy"}`}>
                  {item.label}
                </NavLink>
              ))}
              <div className="py-3">
                <LanguageSwitcher compact />
              </div>
              <Link to="/book-consultation" data-testid="mobile-book-consultation-btn"
                className="mt-3 bg-navy text-ivory text-center font-semibold uppercase tracking-wider text-xs px-6 py-3.5">
                {t("nav.book")}
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet context={{ profile }} />
      </main>

      <footer data-testid="public-footer" className="bg-navy text-ivory/80 hero-grain relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 relative z-10">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-9 w-9 bg-gold flex items-center justify-center">
                <Scale className="h-4 w-4 text-navy" />
              </span>
              <span className="font-serif text-lg font-semibold text-ivory">{firm}</span>
            </div>
            <p className="text-sm leading-relaxed text-ivory/60">
              {profile?.tagline}
            </p>
            <div className="flex gap-3 mt-6">
              {profile?.social?.linkedin && (
                <a href={profile.social.linkedin} target="_blank" rel="noreferrer" data-testid="footer-linkedin"
                  className="h-9 w-9 border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
              {profile?.social?.twitter && (
                <a href={profile.social.twitter} target="_blank" rel="noreferrer" data-testid="footer-twitter"
                  className="h-9 w-9 border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                  <Twitter className="h-4 w-4" />
                </a>
              )}
              {profile?.social?.facebook && (
                <a href={profile.social.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook"
                  className="h-9 w-9 border border-ivory/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-gold mb-5">{t("footer.practiceAreas")}</h4>
            <ul className="space-y-3 text-sm">
              {(profile?.practice_areas || []).slice(0, 6).map((p) => (
                <li key={p.id}>
                  <Link to="/practice-areas" className="hover:text-gold transition-colors">{p.title}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-gold mb-5">{t("footer.quickLinks")}</h4>
            <ul className="space-y-3 text-sm">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hover:text-gold transition-colors">{item.label}</Link>
                </li>
              ))}
              <li><Link to="/book-consultation" className="hover:text-gold transition-colors">{t("nav.book")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-gold mb-5">{t("footer.contact")}</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3"><MapPin className="h-4 w-4 text-gold shrink-0 mt-0.5" /><span>{profile?.office_address}</span></li>
              <li className="flex gap-3"><Phone className="h-4 w-4 text-gold shrink-0 mt-0.5" /><span>{profile?.phone}</span></li>
              <li className="flex gap-3"><Mail className="h-4 w-4 text-gold shrink-0 mt-0.5" /><span>{profile?.email}</span></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-ivory/10 relative z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ivory/50">
            <p>© {new Date().getFullYear()} {firm}. {t("footer.rights")}</p>
            <p>{t("footer.disclaimer")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
